import { useState, useEffect, useMemo } from 'react';
import { notesApiService } from '../services/notesApi';
import { Evidence, AssociatedItem } from '../types/player';
import { useAuth } from '../contexts/AuthContext';
import { Paperclip, Eye, Trash2, UploadCloud, AlertCircle, File, X, Link2, FileText, TriangleAlert, Axe, Unlink } from 'lucide-react';
import { AddEvidenceModal } from './AddEvidenceModal';
import { PlayerProfile } from './PlayerProfile';
import { CreatorProfile } from './CreatorProfile';
import { ConfidentialEvidencePlaceholder } from './ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from './ConfidentialBadge';
import { ConfidentialToggle } from './ConfidentialToggle';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ConfirmationModal } from './ConfirmationModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { SectionHeader } from './ui/SectionHeader';
import { SkeletonLoader } from './SkeletonLoader';

interface EvidenceSectionProps {
  itemId: string;
  itemType: 'note' | 'warning' | 'ban';
}

const LinkedItemCard = ({ item, onUnlink, canUnlink }: { item: AssociatedItem, onUnlink: (targetItemId: string) => void, canUnlink: boolean }) => {
    const getIcon = () => {
        switch (item.type) {
            case 'note': return <span className="icon-tile icon-tile-sm icon-tile-info"><FileText className="w-3.5 h-3.5" /></span>;
            case 'warning': return <span className="icon-tile icon-tile-sm icon-tile-warning"><TriangleAlert className="w-3.5 h-3.5" /></span>;
            case 'ban': return <span className="icon-tile icon-tile-sm icon-tile-danger"><Axe className="w-3.5 h-3.5" /></span>;
            default: return <span className="icon-tile icon-tile-sm"><File className="w-3.5 h-3.5" /></span>;
        }
    };

    const createdAt = new Date(item.created_at);

    return (
        <div className="flex items-start gap-3 py-3">
            {getIcon()}
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                    <span className="text-sm font-medium text-white capitalize">{item.type}</span>
                    <span className="text-[11px] text-glass-400 font-mono">{item.id.substring(0, 12)}…</span>
                    {item.user_uuid && <PlayerProfile uuid={item.user_uuid} showUuid={false} inline />}
                </div>
                <p className="text-sm text-glass-300 truncate mt-1" title={item.content}>{item.content}</p>
                <p className="text-xs text-glass-400 mt-1 flex flex-wrap items-center gap-x-1">
                    <span>By</span> <CreatorProfile creatorId={item.creator} inline />
                    <span className="text-glass-500">·</span>
                    <span className="tabular-nums">{formatDistanceToNow(createdAt, { addSuffix: true })}</span>
                </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <Link to={`/player/${item.user_uuid}`} className="glass-button-secondary min-h-0 h-7 px-2 text-xs">View Player</Link>
                {canUnlink && (
                    <button
                        onClick={() => onUnlink(item.id)}
                        className="btn-icon btn-icon-sm text-glass-400 hover:text-red-400"
                        title="Unlink Item"
                        aria-label="Unlink Item"
                    >
                        <Unlink className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export const EvidenceSection = ({ itemId, itemType }: EvidenceSectionProps) => {
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [linkedItems, setLinkedItems] = useState<AssociatedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Evidence | null>(null);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [itemToUnlink, setItemToUnlink] = useState<AssociatedItem | null>(null);
  const { identity } = useAuth();
  const isMobile = useIsMobile();

  const canDelete = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Notes_Editor') || groups.has('SPY_Super_admin');
  }, [identity]);

  const canEdit = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Notes') || groups.has('SPY_Notes_Editor') || groups.has('SPY_Super_admin');
  }, [identity]);

  const fetchDossier = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notesApiService.getDossierForItem(itemId);
      setEvidence(data.evidence || []);
      setLinkedItems(data.linkedItems || []);
    } catch (err) {
      setError('Failed to load dossier.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDossier();
  }, [itemId]);

  const handleDelete = async (evidenceId: string) => {
    if (window.confirm('Are you sure you want to delete this evidence?')) {
      try {
        await notesApiService.deleteEvidence(evidenceId);
        setEvidence(prev => prev.filter(e => e.id !== evidenceId));
      } catch (err) {
        alert('Failed to delete evidence.');
      }
    }
  };

  const handleToggleConfidential = async (evidenceId: string, newValue: boolean) => {
    try {
      const updated = await notesApiService.patchEvidence(evidenceId, { confidential: newValue });
      setEvidence(prev => prev.map(e => e.id === evidenceId ? updated : e));
    } catch {
      alert('Failed to update confidentiality.');
    }
  };

  const handleUnlinkRequest = (item: AssociatedItem) => {
    setItemToUnlink(item);
    setIsUnlinkModalOpen(true);
  };

  const handleUnlinkConfirm = async () => {
    if (!itemToUnlink) return;

    try {
        await notesApiService.unlinkItem(itemId, itemToUnlink.id);
        setLinkedItems(prev => prev.filter(item => item.id !== itemToUnlink.id));
    } catch (err) {
        alert('Failed to unlink item.');
    } finally {
        setIsUnlinkModalOpen(false);
        setItemToUnlink(null);
    }
  };

  const handleView = (item: Evidence) => {
    if (isEvidenceRedacted(item, identity)) return;
    if (item.content_type.startsWith('image/')) {
        setSelectedImage(item);
    } else {
        window.open(getEvidenceViewUrl(item.id), '_blank');
    }
  };

  const onUploadSuccess = () => {
    fetchDossier(); // Refetch everything
    setIsModalOpen(false);
  }

  return (
    <div className="subcard p-3 md:p-4 space-y-4">
      <div>
        <SectionHeader
            title="Evidence"
            icon={Paperclip}
            count={evidence.length}
            className="mb-2"
            actions={
                <button onClick={() => setIsModalOpen(true)} className="glass-button-sm shrink-0">
                    <UploadCloud className="w-3.5 h-3.5" /> Add/Link Evidence
                </button>
            }
        />

        {loading && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" aria-busy="true">
                {[0, 1, 2, 3].map((i) => (
                    <SkeletonLoader key={i} className="aspect-square w-full rounded-lg" />
                ))}
            </div>
        )}
        {error && (
            <div className="alert alert-danger py-2">
                <AlertCircle /> <span>{error}</span>
            </div>
        )}

        {!loading && !error && evidence.length === 0 && (
            <p className="text-xs text-glass-500 py-1">No evidence attached.</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {evidence.map(item => {
            const redacted = isEvidenceRedacted(item, identity);
            return (
            <div key={item.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-dark-900/40 border border-glass-200/10">
                    {redacted ? (
                        <ConfidentialEvidencePlaceholder />
                    ) : item.content_type.startsWith('image/') ? (
                        <img
                            src={getEvidenceViewUrl(item.id)}
                            alt={item.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-glass-400 p-2">
                            <File className="w-7 h-7" />
                            <span className="text-[11px] text-center font-mono truncate max-w-full mt-2">{item.filename}</span>
                        </div>
                    )}
                </div>
                {!redacted && (
                    <div className={cn("absolute inset-0 rounded-lg bg-black/70 transition-opacity flex items-center justify-center gap-2", isMobile ? "opacity-100 bg-black/40" : "opacity-0 group-hover:opacity-100")}>
                        <button
                            onClick={() => handleView(item)}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-blue-500/60 text-on-solid transition-colors"
                            title="View"
                            aria-label="View evidence"
                        >
                            <Eye className="w-4 h-4" />
                        </button>
                        {canEdit && (
                            <ConfidentialToggle
                                currentValue={item.confidential}
                                onToggle={(newValue) => handleToggleConfidential(item.id, newValue)}
                            />
                        )}
                        {canDelete && (
                            <button
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-red-500/70 text-on-solid transition-colors"
                                title="Delete"
                                aria-label="Delete evidence"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}
                {item.confidential && !redacted && (
                    <div className="absolute top-1.5 right-1.5">
                        <ConfidentialBadge />
                    </div>
                )}
            </div>
            );
            })}
        </div>
      </div>

      <div>
        <SectionHeader title="Linked Items" icon={Link2} count={linkedItems.length} className="mb-1" />
        {!loading && !error && linkedItems.length === 0 && (
            <p className="text-xs text-glass-500 py-1">No items linked.</p>
        )}
         <div className="divide-y divide-glass-200/10">
            {linkedItems.map(item => (
                <LinkedItemCard key={item.id} item={item} onUnlink={() => handleUnlinkRequest(item)} canUnlink={canDelete} />
            ))}
        </div>
      </div>

      {isModalOpen && (
        <AddEvidenceModal
          itemId={itemId}
          itemType={itemType}
          onClose={() => setIsModalOpen(false)}
          onSuccess={onUploadSuccess}
        />
      )}

        {isUnlinkModalOpen && itemToUnlink && (
            <ConfirmationModal
                isOpen={isUnlinkModalOpen}
                onClose={() => setIsUnlinkModalOpen(false)}
                onConfirm={handleUnlinkConfirm}
                title="Unlink Item"
                message={`Are you sure you want to unlink this ${itemToUnlink.type}? This action cannot be undone.`}
            />
        )}

      {selectedImage && !isEvidenceRedacted(selectedImage, identity) && (
        <div className="modal-overlay z-50 bg-black/90" onClick={() => setSelectedImage(null)} role="dialog" aria-modal="true" aria-label={selectedImage.filename}>
            <img
                src={getEvidenceViewUrl(selectedImage.id)}
                alt={selectedImage.filename}
                className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
            <button className="btn-icon absolute top-4 right-4 text-on-solid hover:text-white hover:bg-white/15" title="Close" aria-label="Close">
                <X className="w-5 h-5" />
            </button>
        </div>
      )}
    </div>
  );
};
